import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import isFunction from "lodash/isFunction";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import styles from "./AlertInline.module.css";

export interface AlertInlineProps {
  message: string | TrustedHTML;
  severity: "error" | "warning" | "info";
  variant?: "filled" | "outlined";
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  showIcon?: boolean;
}

const AlertInline = React.forwardRef<HTMLDivElement, AlertInlineProps>(
  function AlertInline(
    {
      message,
      severity,
      variant = "outlined",
      onClick,
      className,
      style,
      children,
      showIcon = true,
    }: AlertInlineProps,
    ref
  ) {
    const Icon = severity === "info" ? InfoOutlinedIcon : WarningIcon;

    return (
      <Box
        ref={ref}
        style={style}
        onClick={onClick}
        className={toClassName(styles.container, className, {
          [styles.borderError]: severity === "error" && variant === "outlined",
          [styles.borderWarning]:
            severity === "warning" && variant === "outlined",
          [styles.borderInfo]: severity === "info" && variant === "outlined",
          [styles.filledError]: severity === "error" && variant === "filled",
          [styles.filledWarning]:
            severity === "warning" && variant === "filled",
          [styles.filledInfo]: severity === "info" && variant === "filled",
          [styles.clickable]: isFunction(onClick),
        })}
      >
        {showIcon && (
          <Icon
            className={toClassName(styles.alertIcon, {
              [styles.colorError]: severity === "error",
              [styles.colorWarning]: severity === "warning",
              [styles.colorInfo]: severity === "info",
              [styles.colorDefault]: variant === "filled",
            })}
          />
        )}

        <Typography
          className={toClassName(styles.alertMessage, {
            [styles.colorError]: severity === "error",
            [styles.colorWarning]: severity === "warning",
            [styles.colorInfo]: severity === "info",
            [styles.colorDefault]: variant === "filled",
            [styles.cta]: isFunction(onClick),
          })}
          dangerouslySetInnerHTML={{ __html: message }}
        />
        {children}
      </Box>
    );
  }
);

export default AlertInline;
